/**
 * DialogHeader - Header section for dialogs.
 *
 * Typically contains a title and close button.
 */

import { X } from 'lucide-react';
import { type ReactNode } from 'react';
import { Button } from '@lean-agility/design-system/react';
import { CardHeader, CardTitle } from '@lean-agility/design-system/react';
import { cn } from '@lean-agility/design-system/react';

export interface DialogHeaderProps {
  /**
   * Title text or custom title content.
   */
  title: ReactNode;
  /**
   * Callback when close button is clicked.
   * If provided, a close button will be automatically rendered.
   */
  onClose?: () => void;
  /**
   * Id to place on the title, for the dialog's `aria-labelledby`.
   *
   * Applied whatever the title's shape, so a caller passing a custom node
   * cannot accidentally leave the dialog without an accessible name.
   */
  titleId?: string;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

export function DialogHeader({ title, onClose, titleId, className }: DialogHeaderProps) {
  return (
    <CardHeader className={cn('flex flex-row items-center justify-between', className)}>
      {typeof title === 'string' ? <CardTitle id={titleId}>{title}</CardTitle> : <div id={titleId}>{title}</div>}
      {onClose && (
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
          <X className="size-4" />
        </Button>
      )}
    </CardHeader>
  );
}
